import React, { useState, useEffect } from 'react';
import { Mail, Send, X } from 'lucide-react';
import Modal from '../common/modals/Modal';
import { useTheme } from '../../context/ThemeContext';
import { getEmailTemplate, openMailto, PLACEHOLDERS } from './useAuditEmailTemplate';

const EmailComposeModal = ({ isOpen, onClose, property, contact, clientTemplates = {} }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (isOpen && property && contact) {
      const tpl = getEmailTemplate(property, contact, clientTemplates);
      setSubject(tpl.subject);
      setBody(tpl.body);
    }
  }, [isOpen, property, contact, clientTemplates]);

  const handleSend = () => {
    openMailto(contact?.email, subject, body);
    onClose();
  };

  const insertPlaceholder = (placeholder) => {
    // Insert at cursor in body textarea (or append)
    setBody((prev) => prev + placeholder);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Compose Email"
      size="xl"
      footer={
        <>
          <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-rasa ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
            Cancel
          </button>
          <button onClick={handleSend} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-rasa bg-blue-600 hover:bg-blue-700 text-white">
            <Send size={14} /> Open in Email
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* To */}
        <div>
          <label className={`text-[11px] font-rasa font-bold uppercase ${d ? 'text-gray-400' : 'text-gray-500'}`}>To</label>
          <div className={`mt-1 flex items-center gap-2 px-3 py-2 rounded-lg ${d ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
            <Mail size={13} className={d ? 'text-gray-500' : 'text-gray-400'} />
            <span className="text-sm font-rasa">{contact?.email || ''}</span>
            {contact?.name && <span className={`text-xs font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>({contact.name})</span>}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className={`text-[11px] font-rasa font-bold uppercase ${d ? 'text-gray-400' : 'text-gray-500'}`}>Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm font-rasa ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
          />
        </div>

        {/* Body */}
        <div>
          <label className={`text-[11px] font-rasa font-bold uppercase ${d ? 'text-gray-400' : 'text-gray-500'}`}>Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm font-rasa leading-relaxed ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y`}
          />
        </div>

        {/* Property context */}
        <div className={`flex items-center gap-3 text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>
          <span>Property: <strong className={d ? 'text-gray-300' : 'text-gray-600'}>{property?.property_name}</strong></span>
          {property?.status && <span>Status: {property.status.replace(/_/g, ' ')}</span>}
          <span className="ml-auto">Edit freely — this opens in your email app</span>
        </div>
      </div>
    </Modal>
  );
};

export default EmailComposeModal;
